/**
 * Comparison Detail Modal for TransactionAnalyzer
 * Shows detailed cost breakdown comparisons between current and previous periods
 */

import React from 'react';
import type { MarketplaceCode, TransactionData } from '../../types/transaction';
import { normalizeAdjustmentDescription, normalizeInventoryFeeDescription } from '../../services/analytics/calculations';
import { classifyAmazonCost } from '../../services/analytics/productAnalytics';
import { translateDescription } from './helpers';
import type { DetailedAnalytics } from '../../services/analytics/analyticsEngine';
import { convertTransactionValue, inAnalyticsScope, toDateOnly } from '../../services/analytics/analyticsEngine';
import { useModalA11y } from '../../hooks/useModalA11y';

type ComparisonDetailType = 'adjustments' | 'inventory' | 'service' | 'fbaCostBreakdown';

interface ComparisonRanges {
  previousPeriod?: { start: Date; end: Date };
  previousYear?: { start: Date; end: Date };
}

interface ComparisonDetailModalProps {
  isOpen: boolean;
  detailType: ComparisonDetailType | null;
  comparisonAnalytics: DetailedAnalytics | null;
  analytics: DetailedAnalytics | null;
  comparisonMode: 'none' | 'previous-period' | 'previous-year';
  comparisonRanges: ComparisonRanges | null;
  allData: TransactionData[];
  /** Same marketplace filter as the cards — rows outside it never reach the modal (A1). */
  marketplaceCode: MarketplaceCode | null;
  dateRange: { start: string; end: string };
  formatMoney: (value: number) => string;
  onClose: () => void;
}

const ComparisonDetailModal: React.FC<ComparisonDetailModalProps> = ({
  isOpen,
  detailType,
  comparisonAnalytics,
  analytics,
  comparisonMode,
  comparisonRanges,
  allData,
  marketplaceCode,
  dateRange,
  formatMoney,
  onClose,
}) => {
  const modalRef = useModalA11y(isOpen, onClose);

  if (!isOpen || !detailType || !comparisonAnalytics || !analytics) return null;

  const compRange = comparisonMode === 'previous-period'
    ? comparisonRanges?.previousPeriod
    : comparisonRanges?.previousYear;

  if (!compRange) return null;

  // Same scope + currency as the cards (calculateAnalytics): marketplace filter, dateOnly with the
  // end day inclusive, and USD conversion when "All" is selected (audit 2026-09-24, A1).
  const curStart = toDateOnly(dateRange.start ? new Date(dateRange.start) : null);
  const curEnd = toDateOnly(dateRange.end ? new Date(dateRange.end) : null);
  const prevStart = toDateOnly(compRange.start);
  const prevEnd = toDateOnly(compRange.end);
  const inCurrent = (item: TransactionData) => inAnalyticsScope(item, marketplaceCode, curStart, curEnd);
  const inPrev = (item: TransactionData) => inAnalyticsScope(item, marketplaceCode, prevStart, prevEnd);
  const totalOf = (item: TransactionData) => convertTransactionValue(item.total, item, marketplaceCode);

  const calculateCostItems = (): { label: string; current: number; prev: number; isReimbursement?: boolean }[] => {
    let costItems: { label: string; current: number; prev: number; isReimbursement?: boolean }[] = [];

    if (detailType === 'adjustments') {
      const currentData = allData.filter(item => inCurrent(item) && item.categoryType === 'Adjustment');
      const prevData = allData.filter(item => inPrev(item) && item.categoryType === 'Adjustment');

      const currentGroups: Record<string, number> = {};
      currentData.forEach(item => {
        const key = normalizeAdjustmentDescription(item.description || 'Other');
        currentGroups[key] = (currentGroups[key] || 0) + totalOf(item);
      });

      const prevGroups: Record<string, number> = {};
      prevData.forEach(item => {
        const key = normalizeAdjustmentDescription(item.description || 'Other');
        prevGroups[key] = (prevGroups[key] || 0) + totalOf(item);
      });

      const allKeys = new Set([...Object.keys(currentGroups), ...Object.keys(prevGroups)]);
      costItems = Array.from(allKeys).map(key => {
        const currentRaw = currentGroups[key] || 0;
        const prevRaw = prevGroups[key] || 0;
        // Dominant value pozitifse reimbursement (para geri geliyor), negatifse maliyet/geri alma
        const dominant = Math.abs(currentRaw) > Math.abs(prevRaw) ? currentRaw : prevRaw;
        return {
          label: key,
          current: Math.abs(currentRaw),
          prev: Math.abs(prevRaw),
          isReimbursement: dominant > 0
        };
      });
    } else if (detailType === 'inventory') {
      // Inventory fees now live in 'FBA Transaction Fee' (helpers.ts isFbaFeeType); both read for safety
      const isFbaFee = (item: TransactionData) =>
        item.categoryType === 'FBA Transaction Fee' || item.categoryType === 'FBA Inventory Fee';
      const currentData = allData.filter(item => inCurrent(item) && isFbaFee(item));
      const prevData = allData.filter(item => inPrev(item) && isFbaFee(item));

      const currentGroups: Record<string, number> = {};
      currentData.forEach(item => {
        const key = normalizeInventoryFeeDescription(item.description, item.orderId);
        currentGroups[key] = (currentGroups[key] || 0) + totalOf(item);
      });

      const prevGroups: Record<string, number> = {};
      prevData.forEach(item => {
        const key = normalizeInventoryFeeDescription(item.description, item.orderId);
        prevGroups[key] = (prevGroups[key] || 0) + totalOf(item);
      });

      const allKeys = new Set([...Object.keys(currentGroups), ...Object.keys(prevGroups)]);
      costItems = Array.from(allKeys).map(key => ({
        label: key,
        current: Math.abs(currentGroups[key] || 0),
        prev: Math.abs(prevGroups[key] || 0)
      }));
    } else if (detailType === 'service') {
      // FBA cost part of 'Service Fee' — the shared classification (ads and advertiser refunds are advertising)
      const isServiceFbaCost = (item: TransactionData) =>
        item.categoryType === 'Service Fee' && classifyAmazonCost(item.categoryType, item.descriptionLower) === 'fbaCost';

      const currentData = allData.filter(item => inCurrent(item) && isServiceFbaCost(item));
      const prevData = allData.filter(item => inPrev(item) && isServiceFbaCost(item));

      const currentGroups: Record<string, number> = {};
      currentData.forEach(item => {
        const key = item.description || 'Other';
        currentGroups[key] = (currentGroups[key] || 0) + totalOf(item);
      });

      const prevGroups: Record<string, number> = {};
      prevData.forEach(item => {
        const key = item.description || 'Other';
        prevGroups[key] = (prevGroups[key] || 0) + totalOf(item);
      });

      const allKeys = new Set([...Object.keys(currentGroups), ...Object.keys(prevGroups)]);
      costItems = Array.from(allKeys).map(key => ({
        label: key,
        current: Math.abs(currentGroups[key] || 0),
        prev: Math.abs(prevGroups[key] || 0)
      }));
    } else if (detailType === 'fbaCostBreakdown') {
      const isFbaCost = (d: TransactionData) => classifyAmazonCost(d.categoryType, d.descriptionLower) === 'fbaCost';

      const currentData = allData.filter(inCurrent);
      const prevData = allData.filter(inPrev);

      const sumOf = (data: TransactionData[], match: (d: TransactionData) => boolean) =>
        Math.abs(data.filter(d => isFbaCost(d) && match(d)).reduce((sum, d) => sum + totalOf(d), 0));
      const calcCosts = (data: TransactionData[]) => ({
        adjustment: sumOf(data, d => d.categoryType === 'Adjustment'),
        inventory: sumOf(data, d => d.categoryType === 'FBA Inventory Fee'),
        chargeback: sumOf(data, d => d.categoryType === 'Chargeback Refund'),
        service: sumOf(data, d => d.categoryType === 'Service Fee'),
        fbaOther: sumOf(data, d => d.categoryType === 'Other' || d.categoryType === 'Others'),
        fbaTransaction: sumOf(data, d => d.categoryType === 'FBA Transaction Fee'),
        feeAdjustment: sumOf(data, d => d.categoryType === 'Fee Adjustment'),
        safet: sumOf(data, d => d.categoryType === 'SAFE-T Reimbursement'),
        liquidations: sumOf(data, d => d.categoryType === 'Liquidations'),
      });

      const current = calcCosts(currentData);
      const prev = calcCosts(prevData);

      costItems = [
        { label: 'Adjustment', current: current.adjustment, prev: prev.adjustment },
        { label: 'FBA Inventory Fee', current: current.inventory, prev: prev.inventory },
        { label: 'Chargeback Refund', current: current.chargeback, prev: prev.chargeback },
        { label: 'Service Fee', current: current.service, prev: prev.service },
        { label: 'FBA fees (Other)', current: current.fbaOther, prev: prev.fbaOther },
        { label: 'FBA Transaction Fee', current: current.fbaTransaction, prev: prev.fbaTransaction },
        { label: 'Fee Adjustment', current: current.feeAdjustment, prev: prev.feeAdjustment },
        { label: 'SAFE-T Reimbursement', current: current.safet, prev: prev.safet },
        { label: 'Liquidations', current: current.liquidations, prev: prev.liquidations },
      ];

      costItems = costItems.filter(item => item.current > 0 || item.prev > 0);
    }

    costItems.sort((a, b) => b.current - a.current);
    return costItems;
  };

  const costItems = calculateCostItems();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 no-print" onClick={onClose}>
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="comparison-detail-modal-title"
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <h2 id="comparison-detail-modal-title" className="text-2xl font-bold text-slate-800">
            {detailType === 'adjustments' && 'Adjustment Comparison'}
            {detailType === 'inventory' && 'FBA Transaction Fee Comparison'}
            {detailType === 'service' && 'Service Fee Comparison'}
            {detailType === 'fbaCostBreakdown' && 'FBA Cost Breakdown Comparison'}
          </h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          <p className="text-sm text-slate-600 mb-4">Current period vs {comparisonAnalytics.label}</p>

          <div className="space-y-3">
            {costItems.map((item, idx) => {
              const change = item.prev !== 0 ? ((item.current - item.prev) / item.prev * 100) : 0;
              const isIncrease = item.current > item.prev;
              const isDecrease = item.current < item.prev;
              // Reimbursement: artış = yeşil (daha fazla para geri), düşüş = kırmızı
              // Maliyet: artış = kırmızı (daha fazla gider), düşüş = yeşil
              const isPositiveChange = item.isReimbursement ? isIncrease : isDecrease;
              const isNegativeChange = item.isReimbursement ? isDecrease : isIncrease;

              return (
                <div key={idx} className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="font-medium text-slate-700 text-sm">{translateDescription(item.label)}</h5>
                    <span className={`text-xs font-bold px-2 py-1 rounded ${
                      isPositiveChange ? 'bg-green-100 text-green-700' : isNegativeChange ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'
                    }`}>
                      {item.prev !== 0 ? `${isIncrease ? '+' : ''}${change.toFixed(1)}%` : 'N/A'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-blue-50 rounded p-3">
                      <p className="text-xs text-blue-600 mb-1">Current Period</p>
                      <p className="text-lg font-bold text-blue-900">{formatMoney(item.current)}</p>
                    </div>
                    <div className="bg-slate-100 rounded p-3">
                      <p className="text-xs text-slate-600 mb-1">Previous Period</p>
                      <p className="text-lg font-bold text-slate-700">{formatMoney(item.prev)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonDetailModal;
